import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	CardFooter,
} from '@/components/ui/card';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/components/ui/form';
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
} from '@/components/ui/input-otp';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import useValidateResetPassword from '../hooks/useValidateResetPassword';
import { hash } from '@/utils/hash';
import { toast } from 'sonner';

const ResetPasswordConfirm = () => {
	const { t } = useTranslation();
	const { state } = useLocation();
	const navigate = useNavigate();
	const [showPasswordForm, setShowPasswordForm] = useState(false);
	const [verificationCode, setVerificationCode] = useState<string>('');

	const { validateResetPassword, validateResetPasswordLoading } =
		useValidateResetPassword();

	const email = state?.email;

	useEffect(() => {
		if (!email) {
			navigate('/reset-password');
		}
	}, [email, navigate]);

	const codeSchema = z.object({
		code: z
			.string()
			.length(5, {
				message: t('validate.error.invalid'),
			})
			.regex(/^\d+$/, {
				message: t('validate.error.invalid'),
			}),
	});

	const codeForm = useForm<z.infer<typeof codeSchema>>({
		resolver: zodResolver(codeSchema),
		defaultValues: {
			code: '',
		},
	});

	const passwordSchema = z
		.object({
			password: z.string().min(8, t('password.form.error.length')),
			confirmPassword: z.string().min(1, t('common.error.required')),
		})
		.refine((data) => data.password === data.confirmPassword, {
			message: t('password.form.error.match'),
			path: ['confirmPassword'],
		});

	const passwordForm = useForm<z.infer<typeof passwordSchema>>({
		resolver: zodResolver(passwordSchema),
		defaultValues: {
			password: '',
			confirmPassword: '',
		},
	});

	function handleCodeSubmit(values: z.infer<typeof codeSchema>) {
		const valid = codeForm.trigger();

		if (!valid) return;

		setVerificationCode(values.code);
		setShowPasswordForm(true);
	}

	function handlePasswordSubmit(values: z.infer<typeof passwordSchema>) {
		const valid = passwordForm.trigger();

		if (!valid) return;

		validateResetPassword(
			{
				email,
				code: verificationCode,
				password: hash(values.password),
			},
			{
				onSuccess() {
					toast.success(t('password.form.success'));
					navigate('/home');
				},
				onError() {
					codeForm.setError('code', {
						message: t('validate.error.invalid'),
					});
					setShowPasswordForm(false);
				},
			}
		);
	}

	return (
		<>
			<Card className="mx-auto -mt-9 min-w-96 max-w-sm self-center">
				<Link to="/reset-password" className="-mt-10 flex p-2 pl-0">
					<ChevronLeft />
					{t('back')}
				</Link>
				<CardHeader>
					<CardTitle className="text-xl">
						{showPasswordForm
							? t('password.form.title')
							: t('resetPassword.confirm.code.title')}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{!showPasswordForm ? (
						<Form {...codeForm}>
							<form
								onSubmit={codeForm.handleSubmit(
									handleCodeSubmit
								)}
								className="flex w-full justify-center space-y-6"
							>
								<FormField
									control={codeForm.control}
									name="code"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<InputOTP
													maxLength={5}
													onComplete={() =>
														handleCodeSubmit(
															codeForm.getValues()
														)
													}
													{...field}
												>
													<InputOTPGroup>
														<InputOTPSlot
															className="h-14 w-14 text-lg"
															index={0}
														/>
														<InputOTPSlot
															className="h-14 w-14 text-lg"
															index={1}
														/>
														<InputOTPSlot
															className="h-14 w-14 text-lg"
															index={2}
														/>
														<InputOTPSlot
															className="h-14 w-14 text-lg"
															index={3}
														/>
														<InputOTPSlot
															className="h-14 w-14 text-lg"
															index={4}
														/>
													</InputOTPGroup>
												</InputOTP>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</form>
						</Form>
					) : (
						<Form {...passwordForm}>
							<form
								onSubmit={passwordForm.handleSubmit(
									handlePasswordSubmit
								)}
								className="flex flex-col space-y-4"
							>
								<div className="grid gap-4">
									<FormField
										control={passwordForm.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<Input
														label={t(
															'password.form.new.title'
														)}
														type="password"
														{...field}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={passwordForm.control}
										name="confirmPassword"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<Input
														label={t(
															'password.form.confirm.title'
														)}
														type="password"
														{...field}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<Button
									className="w-full"
									type="submit"
									isLoading={validateResetPasswordLoading}
								>
									{t('password.form.submit')}
								</Button>
							</form>
						</Form>
					)}
				</CardContent>
				{!showPasswordForm && (
					<CardFooter>
						<CardDescription>
							{t('resetPassword.confirm.code.description')}
						</CardDescription>
					</CardFooter>
				)}
			</Card>
		</>
	);
};

export default ResetPasswordConfirm;
